/**
 * viewFilterStore — Zustand store for view-level filters.
 *
 * Provides filter criteria for task lists in ViewShell. This complements
 * the existing tagFilterStore (which handles tag chip bar + text search).
 *
 * Filter categories:
 *   - status: show Completed / Canceled tasks in active views
 *   - hasDueDate: only show tasks with/without a deadline
 *   - overdue: only show overdue tasks
 *   - userStatus: filter by custom user-defined status IDs
 *   - sort: override the default view sort order
 *
 * Filters are ephemeral — reset on view navigation.
 */

import { create } from "zustand"
import type { ViewItem } from "@/features/views/hooks/useViewData"
import { ObjectStatus } from "@/db/schema"
import { parseDbDate } from "@totodo/operations"
import dayjs from "dayjs"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SortOption =
  | "default" // View-native sort order
  | "title-asc"
  | "title-desc"
  | "created-newest"
  | "created-oldest"
  | "due-date"

export interface ViewFilterCriteria {
  /** Show completed tasks (normally hidden in active views) */
  showCompleted: boolean
  /** Show canceled tasks (normally hidden in active views) */
  showCanceled: boolean
  /** Only show tasks that have a deadline set */
  hasDueDate: boolean | null // null = no filter
  /** Only show overdue tasks */
  overdueOnly: boolean
  /** Filter by specific user status IDs (empty = no filter) */
  userStatuses: string[]
  /** Filter by area ID (null = no filter, show all areas) */
  areaId: string | null
  /** Sort override */
  sort: SortOption
}

interface ViewFilterState extends ViewFilterCriteria {
  // ── Actions ──
  setShowCompleted: (show: boolean) => void
  setShowCanceled: (show: boolean) => void
  setHasDueDate: (value: boolean | null) => void
  setOverdueOnly: (value: boolean) => void
  setUserStatuses: (ids: string[]) => void
  toggleUserStatus: (id: string) => void
  setAreaId: (id: string | null) => void
  setSort: (sort: SortOption) => void
  /** Reset all filters to default */
  reset: () => void
  /** Whether any non-default filter is active */
  isFiltering: () => boolean
}

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

const DEFAULTS: ViewFilterCriteria = {
  showCompleted: false,
  showCanceled: false,
  hasDueDate: null,
  overdueOnly: false,
  userStatuses: [],
  areaId: null,
  sort: "default",
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useViewFilterStore = create<ViewFilterState>((set, get) => ({
  ...DEFAULTS,

  setShowCompleted: (show) => set({ showCompleted: show }),
  setShowCanceled: (show) => set({ showCanceled: show }),
  setHasDueDate: (value) => set({ hasDueDate: value }),
  setOverdueOnly: (value) => set({ overdueOnly: value }),
  setUserStatuses: (ids) => set({ userStatuses: ids }),
  toggleUserStatus: (id) => {
    const current = get().userStatuses
    set({
      userStatuses: current.includes(id)
        ? current.filter((s) => s !== id)
        : [...current, id],
    })
  },
  setAreaId: (id) => set({ areaId: id }),
  setSort: (sort) => set({ sort }),
  reset: () => set(DEFAULTS),
  isFiltering: () => {
    const s = get()
    return (
      s.showCompleted !== DEFAULTS.showCompleted ||
      s.showCanceled !== DEFAULTS.showCanceled ||
      s.hasDueDate !== DEFAULTS.hasDueDate ||
      s.overdueOnly !== DEFAULTS.overdueOnly ||
      s.userStatuses.length > 0 ||
      s.areaId !== DEFAULTS.areaId ||
      s.sort !== DEFAULTS.sort
    )
  },
}))

// ---------------------------------------------------------------------------
// Pure filter + sort functions (used by ViewShell)
// ---------------------------------------------------------------------------

/**
 * Apply view filter criteria to a list of items.
 *
 * Note: `showCompleted` / `showCanceled` work as *inclusion* — they
 * ADD completed/canceled items to the result. Most views already
 * exclude them in the query layer, so this is a no-op by default.
 * When enabled, ViewShell must re-query or the query layer must
 * include them. For now, we filter the opposite direction: if NOT
 * showing completed, filter them OUT. This handles logbook/trash
 * where completed items are already in the data.
 */
export function applyViewFilters<T extends ViewItem>(
  items: T[],
  criteria: ViewFilterCriteria,
  /** projectId → areaId map (from ancestorTagStore). Required when areaId filter is set. */
  projectToAreaMap?: Record<string, string>,
): T[] {
  let result = items

  // Status filters — filter OUT statuses that shouldn't be shown
  // Recurring templates always pass status filters (they use Completed status
  // internally for FIXED mode but should remain visible in their container).
  if (!criteria.showCompleted) {
    result = result.filter(
      (item) =>
        item.task.status !== ObjectStatus.Completed ||
        item.task.isRecurringTemplate === 1,
    )
  }
  if (!criteria.showCanceled) {
    result = result.filter(
      (item) =>
        item.task.status !== ObjectStatus.Canceled ||
        item.task.isRecurringTemplate === 1,
    )
  }

  // Deadline filter
  if (criteria.hasDueDate === true) {
    result = result.filter((item) => !!item.task.dueAt)
  } else if (criteria.hasDueDate === false) {
    result = result.filter((item) => !item.task.dueAt)
  }

  // Overdue filter
  if (criteria.overdueOnly) {
    const now = dayjs()
    result = result.filter(
      (item) => {
        const due = parseDbDate(item.task.dueAt)
        return due !== undefined && dayjs(due).isBefore(now)
      },
    )
  }

  // User status filter
  if (criteria.userStatuses.length > 0) {
    const statusSet = new Set(criteria.userStatuses)
    result = result.filter(
      (item) =>
        item.task.userStatus != null &&
        statusSet.has(String(item.task.userStatus)),
    )
  }

  // Area filter — show only tasks belonging to containers under the selected area
  if (criteria.areaId && projectToAreaMap) {
    result = result.filter((item) => {
      const segs = item.task.parentPath.split("/")
      // segs[1] is the containerId (e.g. "root/{containerId}" or "root/{containerId}/{sectionId}")
      const containerId = segs[1]
      if (!containerId || containerId.startsWith("#")) {
        // Virtual containers (#anyday, #inbox) — show if areaId is "__no-area__"
        return criteria.areaId === "__no-area__"
      }
      const taskAreaId = projectToAreaMap[containerId]
      if (criteria.areaId === "__no-area__") {
        return !taskAreaId
      }
      return taskAreaId === criteria.areaId
    })
  }

  return result
}

/**
 * Apply sort override to items. Only used when sort !== "default".
 * Returns a new sorted array (does not mutate).
 */
export function applySortOverride<T extends ViewItem>(
  items: T[],
  sort: SortOption,
): T[] {
  if (sort === "default") return items

  const sorted = [...items]
  switch (sort) {
    case "title-asc":
      sorted.sort((a, b) =>
        a.task.title.localeCompare(b.task.title, undefined, {
          sensitivity: "base",
        }),
      )
      break
    case "title-desc":
      sorted.sort((a, b) =>
        b.task.title.localeCompare(a.task.title, undefined, {
          sensitivity: "base",
        }),
      )
      break
    case "created-newest":
      sorted.sort((a, b) => Number(b.task.createdAt) - Number(a.task.createdAt))
      break
    case "created-oldest":
      sorted.sort((a, b) => Number(a.task.createdAt) - Number(b.task.createdAt))
      break
    case "due-date":
      sorted.sort((a, b) => {
        const aDue = parseDbDate(a.task.dueAt) ?? Infinity
        const bDue = parseDbDate(b.task.dueAt) ?? Infinity
        return aDue - bDue
      })
      break
  }
  return sorted
}
